if (!customElements.get('byte-sticky-price-bar')) {
  class ByteStickyPriceBar extends HTMLElement {
    constructor() {
      super();

      this.isActive = false;
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandleAvisChange = this.#handleAvisChange.bind(this);
    }

    connectedCallback() {
      this.priceSourceSelector = this.dataset.priceSource;
      this.priceValue = this.querySelector('[data-sticky-price-value]');
      this.compareValue = this.querySelector('[data-sticky-price-compare]');

      this.#waitForAvisOptions();
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#disconnectObservers();
    }

    handleScroll() {
      this.#updateTopOffset();
    }

    handleResize() {
      this.#updateTopOffset();
    }

    #waitForAvisOptions() {
      const avisRoot = document.getElementById('avpoptions-container__v2');

      if (avisRoot) {
        this.#initialize(avisRoot);
        return;
      }

      this.avisWaitObserver = new MutationObserver(() => {
        const root = document.getElementById('avpoptions-container__v2');
        if (!root) return;

        this.avisWaitObserver.disconnect();
        this.avisWaitObserver = null;
        this.#initialize(root);
      });

      this.avisWaitObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    #initialize(avisRoot) {
      this.avisRoot = avisRoot;
      this.priceSource = document.querySelector(this.priceSourceSelector);

      if (!this.priceSource) return;

      this.#cacheHeaderGroup();
      this.#bindEvents();
      this.#observePriceChanges();
      this.#updateTopOffset();
      this.#syncPrice();
      this.#setActive(true);
    }

    #cacheHeaderGroup() {
      this.headerSections = document.querySelectorAll(
        '.announcement-bar-section, .section-header, sticky-header'
      );
    }

    #bindEvents() {
      window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      window.addEventListener('resize', this.boundHandleResize, { passive: true });

      if (this.avisRoot) {
        this.avisRoot.addEventListener('change', this.boundHandleAvisChange);
        this.avisRoot.addEventListener('input', this.boundHandleAvisChange);
        this.avisRoot.addEventListener('click', this.boundHandleAvisChange);
      }
    }

    #unbindEvents() {
      window.removeEventListener('scroll', this.boundHandleScroll);
      window.removeEventListener('resize', this.boundHandleResize);

      if (this.avisRoot) {
        this.avisRoot.removeEventListener('change', this.boundHandleAvisChange);
        this.avisRoot.removeEventListener('input', this.boundHandleAvisChange);
        this.avisRoot.removeEventListener('click', this.boundHandleAvisChange);
      }

      this.addChargeInput?.removeEventListener('change', this.boundHandleAvisChange);
    }

    #handleAvisChange() {
      window.requestAnimationFrame(() => {
        this.#syncPrice();
      });
    }

    #observePriceChanges() {
      if (!this.priceSource) return;

      this.priceObserver = new MutationObserver(() => {
        this.#syncPrice();
      });

      this.priceObserver.observe(this.priceSource, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      const addChargeInput = document.querySelector('.apo-total-addcharge');
      if (addChargeInput) {
        this.addChargeInput = addChargeInput;

        this.addChargeObserver = new MutationObserver(() => {
          this.#syncPrice();
        });

        this.addChargeObserver.observe(addChargeInput, {
          attributes: true,
          attributeFilter: ['value'],
        });

        addChargeInput.addEventListener('change', this.boundHandleAvisChange);
      }
    }

    #disconnectObservers() {
      this.priceObserver?.disconnect();
      this.addChargeObserver?.disconnect();
      this.avisWaitObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
    }

    #updateTopOffset() {
      const topOffset = this.#getVisibleHeaderBottom();
      this.style.setProperty('--byte-sticky-price-top', `${topOffset}px`);
    }

    #getVisibleHeaderBottom() {
      let maxBottom = 0;
      const headerSections =
        this.headerSections ||
        document.querySelectorAll('.announcement-bar-section, .section-header, sticky-header');

      for (const section of headerSections) {
        if (!(section instanceof HTMLElement) || section.offsetHeight === 0) continue;

        const style = window.getComputedStyle(section);
        if (style.display === 'none' || style.visibility === 'hidden') continue;

        const rect = section.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0) continue;

        maxBottom = Math.max(maxBottom, rect.bottom);
      }

      return Math.max(0, Math.round(maxBottom));
    }

    #getDisplayPrice() {
      if (!this.priceSource) return { current: '', compare: '' };

      // Avis clones the theme price into `.price__container.apo-variant-price` and hides the
      // original, so prefer the visible clone when it exists.
      const priceContainer =
        this.priceSource.querySelector('.price__container.apo-variant-price') ||
        this.priceSource.querySelector('.price__container');
      if (!priceContainer) return { current: '', compare: '' };

      // Avis writes the option-adjusted total into `.price__sale .price-item--sale` even when the
      // product is not on sale (that block stays hidden), so it is the source of truth.
      const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');
      const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
      const current =
        salePrice?.textContent.trim() || regularPrice?.textContent.trim() || '';

      const isOnSale = Boolean(priceContainer.closest('.price--on-sale'));
      const comparePrice = isOnSale
        ? priceContainer.querySelector('.price__sale s.price-item--regular')
        : null;

      return {
        current,
        compare: comparePrice ? comparePrice.textContent.trim() : '',
      };
    }

    #syncPrice() {
      if (!this.priceValue) return;

      const { current, compare } = this.#getDisplayPrice();
      if (!current) return;

      this.priceValue.textContent = current;

      if (this.compareValue) {
        if (compare) {
          this.compareValue.textContent = compare;
          this.compareValue.hidden = false;
        } else {
          this.compareValue.textContent = '';
          this.compareValue.hidden = true;
        }
      }
    }

    #setActive(isActive) {
      this.isActive = isActive;
      this.classList.toggle('is-active', isActive);
      this.setAttribute('aria-hidden', String(!isActive));
    }
  }

  customElements.define('byte-sticky-price-bar', ByteStickyPriceBar);
}
